import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Banknote, ArrowLeft, Download, Home, 
  TrendingDown, CheckCircle, AlertCircle 
} from "lucide-react";
import { API } from "@/App";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

export default function DownsizingModule({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [userData, setUserData] = useState(user);

  useEffect(() => {
    fetchCalculation();
  }, []);

  const fetchCalculation = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setUserData(userResponse.data);
      
      const response = await axios.get(`${API}/downsizing/calculate`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setResult(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to calculate";
      if (message.includes("onboarding")) {
        toast.error("Please complete onboarding first");
        navigate('/onboarding');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setDownloadLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/downsizing/pdf`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TenURes_Downsizing_${user?.ur_id || 'Guide'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloadLoading(false);
    }
  };

  const currentBeds = userData?.current_bedrooms || 0;
  const neededBeds = userData?.needed_bedrooms || 0;
  const spareBeds = currentBeds - neededBeds;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <TenUResLogo size="text-2xl" />
          <Button 
            variant="ghost" 
            className="text-[#111827] hover:text-[#05D38A]"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Banknote className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2">
              Downsizing Incentives
            </h1>
            <p className="text-gray-600">
              Discover cash bonuses available for rightsizing your home.
            </p>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-[#05D38A] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Current Situation Card */}
              <Card className="bg-white border-gray-200 shadow-lg mb-6">
                <CardHeader>
                  <CardTitle className="text-lg text-[#111827] flex items-center gap-2">
                    <Home className="w-5 h-5 text-gray-400" />
                    Your Current Situation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-[#111827]">{currentBeds}</p>
                      <p className="text-gray-500 text-sm">Current Beds</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-[#111827]">{neededBeds}</p>
                      <p className="text-gray-500 text-sm">Needed Beds</p>
                    </div>
                    <div className={`rounded-lg p-4 ${spareBeds > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                      <p className={`text-3xl font-bold ${spareBeds > 0 ? 'text-amber-600' : 'text-[#111827]'}`}>
                        {spareBeds > 0 ? spareBeds : 0}
                      </p>
                      <p className="text-gray-500 text-sm">Spare Beds</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Result Card */}
              <Card className={`border-2 shadow-lg ${result?.eligible ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
                <CardContent className="pt-6">
                  {result?.eligible ? (
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-amber-600" />
                      </div>
                      
                      <h2 className="text-2xl font-bold text-[#111827] mb-2">
                        You Could Claim a Bonus!
                      </h2>
                      
                      <div className="bg-white rounded-xl p-6 my-6 border border-amber-200">
                        <p className="text-gray-500 text-sm mb-2">Estimated Incentive</p>
                        <p className="text-4xl font-bold text-amber-600">
                          £{result.min_bonus?.toLocaleString()} - £{result.max_bonus?.toLocaleString()}
                        </p>
                        <p className="text-gray-500 text-sm mt-2">
                          Based on {result.spare_bedrooms} spare bedroom{result.spare_bedrooms > 1 ? 's' : ''}
                        </p>
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-6">
                        {result.message}
                      </p>
                      
                      <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                        <h3 className="text-[#111827] font-medium mb-2 flex items-center gap-2">
                          <TrendingDown className="w-4 h-4 text-[#05D38A]" />
                          Benefits of Downsizing
                        </h3>
                        <ul className="text-gray-600 text-sm space-y-2">
                          <li className="flex items-start gap-2">
                            <span className="text-[#05D38A]">•</span>
                            Cash incentive payment (varies by local authority)
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#05D38A]">•</span>
                            Lower rent and utility bills
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#05D38A]">•</span>
                            Potential removal cost assistance
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#05D38A]">•</span>
                            Decoration allowance for new home
                          </li>
                        </ul>
                      </div>
                      
                      <Button
                        onClick={downloadPDF}
                        className="w-full bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold glow-mint"
                        disabled={downloadLoading}
                        data-testid="download-pdf-btn"
                      >
                        {downloadLoading ? (
                          <div className="w-5 h-5 border-2 border-[#111827] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download Officer Discussion Guide
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-gray-400" />
                      </div>
                      
                      <h2 className="text-xl font-bold text-[#111827] mb-2">
                        No Downsizing Benefit Available
                      </h2>
                      
                      <p className="text-gray-600 mb-4">
                        {result?.reason || "Based on your current situation, you don't have spare bedrooms for downsizing incentives."}
                      </p>
                      
                      <p className="text-gray-500 text-sm">
                        Downsizing bonuses are available when you have more bedrooms than you need. 
                        If your circumstances change, come back and check again.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info Notice */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-6 text-center"
              >
                <p className="text-gray-500 text-sm">
                  Actual amounts vary by local authority. Contact your Housing Officer for current schemes in your area.
                </p>
              </motion.div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
